import React, { useState, useEffect } from 'react';
import { 
  Coins, Plus, Settings, TrendingUp, RefreshCw, Star, Search, 
  Wallet, Users, ArrowUpRight, ArrowDownRight, Gem, ShieldCheck 
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const CoinManagement = () => {
  const [activeTab, setActiveTab] = useState('holdings'); // 'holdings' | 'circulation'
  
  // Circulation Data
  const [circulation, setCirculation] = useState([]);
  const [circLoading, setCircLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // User Coin Holdings Data
  const [userHoldings, setUserHoldings] = useState([]);
  const [holdingsSummary, setHoldingsSummary] = useState({ total_users: 0, total_user_coins: 0, total_user_diamonds: 0 });
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [holdingsSearch, setHoldingsSearch] = useState('');

  // Sellers & Settings
  const [sellers, setSellers] = useState([]);
  const [settings, setSettings] = useState({ coin_rate: '100' });
  const [loading, setLoading] = useState(true);

  // Forms
  const [issueForm, setIssueForm] = useState({ seller_id: '', coins_amount: '', rate: '1.0000' });
  const [rateForm, setRateForm] = useState('100');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchCirculation = async (search = '') => {
    setCircLoading(true);
    try {
      const circRes = await axios.get(`${constant.API_BASE_URL}/admin/business/coins/circulation?search=${encodeURIComponent(search)}`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (circRes.data.success) setCirculation(circRes.data.data);
    } catch (err) {
      console.error("Error fetching circulation:", err);
    } finally {
      setCircLoading(false);
    }
  };

  const fetchUserHoldings = async (search = '') => {
    setHoldingsLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/business/coins/user-holdings?search=${encodeURIComponent(search)}`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setUserHoldings(res.data.data);
        const sum = res.data.summary || res.data.stats || {};
        setHoldingsSummary({
          total_users: sum.total_users ?? 0,
          total_user_coins: sum.total_user_coins ?? sum.total_circulating_coins ?? 0,
          total_user_diamonds: sum.total_user_diamonds ?? sum.total_circulating_diamonds ?? 0
        });
      }
    } catch (err) {
      console.error("Error fetching user coin holdings:", err);
    } finally {
      setHoldingsLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const sellerRes = await axios.get(`${constant.API_BASE_URL}/admin/business/sellers`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      const settingsRes = await axios.get(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      fetchCirculation(searchTerm);
      fetchUserHoldings(holdingsSearch);

      if (sellerRes.data.success) setSellers(sellerRes.data.data.filter(s => s.status === 'approved'));
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.data);
        setRateForm(settingsRes.data.data.coin_rate || '100');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    fetchCirculation(val);
  };

  const handleHoldingsSearchChange = (e) => {
    const val = e.target.value;
    setHoldingsSearch(val);
    fetchUserHoldings(val);
  };

  const handleIssueCoins = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/admin/business/coins/issue`, {
        seller_id: issueForm.seller_id,
        coins_amount: parseInt(issueForm.coins_amount),
        rate: parseFloat(issueForm.rate)
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert("Coins issued to seller successfully!");
        setIssueForm({ seller_id: '', coins_amount: '', rate: '1.0000' });
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to issue coins");
    }
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        key: 'coin_rate',
        value: rateForm
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert("Coin exchange rate updated!");
        fetchData();
      }
    } catch (err) {
      alert("Failed to update rate");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Title Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Coin Management & User Holdings</h2>
          <p className="text-slate-500 mt-1">
            Real-time user coin balances, circulating audit trail, seller allocations, and exchange rates.
          </p>
        </div>
        <button 
          onClick={fetchData}
          className="p-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer"
          title="Refresh All Data"
        >
          <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Main Grid: Controls on left, Tables on right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Issue Coins & Rates Configuration */}
        <div className="space-y-8">
          {/* Issue Coins Form */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Plus size={18} className="text-pink-600" />
              Issue Coins to Seller
            </h3>
            <form onSubmit={handleIssueCoins} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Select Approved Seller</label>
                <select
                  required
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-sm font-semibold"
                  value={issueForm.seller_id}
                  onChange={(e) => setIssueForm({ ...issueForm, seller_id: e.target.value })}
                >
                  <option value="">-- Select Seller --</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.level || 'Seller'})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Coins Amount</label>
                <input
                  required
                  type="number"
                  placeholder="e.g. 50000"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-sm font-semibold"
                  value={issueForm.coins_amount}
                  onChange={(e) => setIssueForm({ ...issueForm, coins_amount: e.target.value })}
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Modifier Rate (Coins / Unit)</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. 1.0000"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-sm font-semibold font-mono"
                  value={issueForm.rate}
                  onChange={(e) => setIssueForm({ ...issueForm, rate: e.target.value })}
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl active:scale-95 transition shadow-lg shadow-pink-200 cursor-pointer text-sm"
              >
                Issue Coins to Seller
              </button>
            </form>
          </div>

          {/* Settings / Rates Configuration Form */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
              <Settings size={20} className="text-slate-700" /> Exchange Rate Settings
            </h3>
            <form onSubmit={handleSaveRate} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Global Coin Rate (Coins per 1 USD)</label>
                <input
                  required
                  type="number"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition font-bold text-lg"
                  value={rateForm}
                  onChange={(e) => setRateForm(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl active:scale-95 transition cursor-pointer text-sm"
              >
                Update Exchange Rate
              </button>
            </form>
          </div>
        </div>

        {/* Right 2 Columns: Tabs for User Coin Holdings vs Circulation History */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Navigation Tabs */}
          <div className="flex items-center gap-3 bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
            <button
              onClick={() => setActiveTab('holdings')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer ${
                activeTab === 'holdings'
                  ? 'bg-gradient-to-r from-pink-600 to-rose-600 text-white shadow-md shadow-pink-200'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Wallet size={16} />
              User Coin Balances & Holdings
            </button>
            <button
              onClick={() => setActiveTab('circulation')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer ${
                activeTab === 'circulation'
                  ? 'bg-slate-900 text-white shadow-md shadow-slate-300'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Coins size={16} />
              Circulation Audit Trail & Ledger
            </button>
          </div>

          {/* TAB 1: USER COIN BALANCES & HOLDINGS */}
          {activeTab === 'holdings' && (
            <div className="space-y-6">
              {/* Summary KPIs */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                    <Users size={22} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Total User Accounts</p>
                    <p className="text-2xl font-black text-slate-800 mt-0.5">
                      {holdingsSummary.total_users?.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                    <Coins size={22} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Coins in Wallets</p>
                    <p className="text-2xl font-black text-amber-600 mt-0.5">
                      {holdingsSummary.total_user_coins?.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                    <Gem size={22} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Total Diamonds</p>
                    <p className="text-2xl font-black text-emerald-600 mt-0.5">
                      {holdingsSummary.total_user_diamonds?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* User Holdings Table Container */}
              <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">User Wallet Coin Holdings</h3>
                    <p className="text-xs text-slate-400 font-medium">Live breakdown of individual user coin & diamond reserves</p>
                  </div>
                  <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      value={holdingsSearch}
                      onChange={handleHoldingsSearchChange}
                      placeholder="Search XOXO ID, Name, Email..."
                      className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/20"
                    />
                  </div>
                </div>

                {holdingsLoading ? (
                  <div className="p-20 text-center flex-1">
                    <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-slate-500">Loading user coin balances...</p>
                  </div>
                ) : userHoldings.length === 0 ? (
                  <div className="p-20 text-center text-slate-400 flex-1">
                    <Wallet size={48} className="mx-auto mb-4 opacity-30" />
                    <p>No user balances found matching "{holdingsSearch}".</p>
                  </div>
                ) : (
                  <div className="overflow-auto flex-1 max-h-[500px] scrollbar-thin scrollbar-thumb-slate-200">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider sticky top-0 bg-slate-50 z-10">
                          <th className="p-4">User</th>
                          <th className="p-4">XOXO ID</th>
                          <th className="p-4">Country</th>
                          <th className="p-4 text-right">Coins Balance</th>
                          <th className="p-4 text-right">Diamonds</th>
                          <th className="p-4 text-right">Last Updated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {userHoldings.map((user) => (
                          <tr key={user.user_id} className="hover:bg-slate-50/50 transition">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <img
                                  src={user.avatar_url || '/xoxo-logo.jpeg'}
                                  alt="avatar"
                                  className="w-9 h-9 rounded-xl object-cover bg-slate-100 border border-slate-100"
                                  onError={(e) => { e.target.src = '/xoxo-logo.jpeg'; }}
                                />
                                <div>
                                  <p className="font-bold text-slate-800 text-xs">
                                    {user.first_name || ''} {user.last_name || ''}
                                  </p>
                                  <p className="text-[10px] text-slate-400 font-mono">
                                    {user.email || user.phone || 'No Contact'}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 font-mono font-bold text-pink-600 text-xs">
                              {user.xoxo_id || user.user_id.substring(0, 8)}
                            </td>
                            <td className="p-4">
                              <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold">
                                {user.country || 'India'}
                              </span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="inline-flex items-center gap-1 font-black text-amber-600 text-sm px-2.5 py-1 rounded-xl bg-amber-50 border border-amber-100">
                                🪙 {Number(user.coins ?? user.coin_balance ?? user.balance ?? 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="inline-flex items-center gap-1 font-black text-emerald-600 text-sm px-2.5 py-1 rounded-xl bg-emerald-50 border border-emerald-100">
                                💎 {Number(user.diamonds ?? user.diamond_balance ?? 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="p-4 text-right text-slate-400 text-xs font-mono">
                              {(user.last_updated || user.lastmodified_at) ? new Date(user.last_updated || user.lastmodified_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: CIRCULATION AUDIT TRAIL */}
          {activeTab === 'circulation' && (
            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Circulation Audit Trail & Ledger</h3>
                  <p className="text-xs text-slate-400 font-medium">Search by Action, Seller ID, XOXO ID, or User Name</p>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search Seller ID / XOXO ID..."
                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/20"
                  />
                </div>
              </div>

              {loading || circLoading ? (
                <div className="p-20 text-center flex-1">
                  <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-500">Loading audit trail...</p>
                </div>
              ) : circulation.length === 0 ? (
                <div className="p-20 text-center text-slate-400 flex-1">
                  <Coins size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No coin movements found matching "{searchTerm}".</p>
                </div>
              ) : (
                <div className="overflow-auto flex-1 max-h-[500px] scrollbar-thin scrollbar-thumb-slate-200">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider sticky top-0 bg-slate-50 z-10">
                        <th className="p-4">Action</th>
                        <th className="p-4">Sender</th>
                        <th className="p-4">Receiver</th>
                        <th className="p-4 text-right">Coins Amount</th>
                        <th className="p-4 text-center">Modifier Rate</th>
                        <th className="p-4 text-right">Transaction Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {circulation.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50/50 transition">
                          <td className="p-4">
                            <span className={`font-bold uppercase text-[10px] px-2 py-0.5 rounded-md ${
                              item.action_type === 'GAME_WIN' 
                                ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                                : item.action_type === 'GAME_BET'
                                ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                : item.action_type === 'RECHARGE'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                : 'bg-indigo-50 text-indigo-600'
                            }`}>
                              {item.action_type}
                            </span>
                            {item.seller_id && (
                              <p className="text-[10px] text-slate-400 font-mono mt-0.5">Seller: {item.seller_id.substring(0, 8)}</p>
                            )}
                          </td>
                          <td className="p-4">
                            <p className="text-slate-700 font-semibold text-xs">{item.sender_first ? `${item.sender_first} ${item.sender_last}` : 'Admin'}</p>
                            {(item.sender_xoxo_id || item.sender_user_id) && (
                              <p className="text-[10px] font-mono font-bold text-pink-600">ID: {item.sender_xoxo_id || item.sender_user_id}</p>
                            )}
                          </td>
                          <td className="p-4">
                            <p className="text-slate-700 font-semibold text-xs">{item.receiver_first ? `${item.receiver_first} ${item.receiver_last}` : 'System'}</p>
                            {(item.receiver_xoxo_id || item.receiver_user_id) && (
                              <p className="text-[10px] font-mono font-bold text-emerald-600">ID: {item.receiver_xoxo_id || item.receiver_user_id}</p>
                            )}
                          </td>
                          <td className="p-4 text-right font-black text-pink-600">
                            {parseInt(item.coins_amount).toLocaleString()}
                          </td>
                          <td className="p-4 text-center text-slate-500 font-mono text-xs">
                            {parseFloat(item.rate || 1.0).toFixed(4)}
                          </td>
                          <td className="p-4 text-right text-slate-400 text-xs font-mono">
                            {new Date(item.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoinManagement;
